/**
 * SR-NET-05 진단 계산(순수 — 시험 대상): 중앙값 · 백분위 · kbps · 항목 판정 · 종합 판정 · 443 단일 포트 판정.
 * 기준은 제어 평면 세션 응답(thresholds — NetcheckService 와 같은 값)을 쓰고, 없으면 {@link DEFAULT_THRESHOLDS}.
 */
import type { Thresholds, Verdict, WssResult } from "../types";

/** NFR NF-002 · §5.3 — RTT ≤150 ms(교내망 입력→반영) · 내려받기 30명 최악 2.5 Mbps · 평균 1.0 Mbps · 올리기 30 × 8 kbps */
export const DEFAULT_THRESHOLDS: Thresholds = {
  rttPassMs: 150,
  rttWarnMs: 300,
  downPassKbps: 2500,
  downWarnKbps: 1000,
  upPassKbps: 240,
  upWarnKbps: 120,
};

export function percentile(xs: readonly number[], p: number): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const i = Math.min(s.length - 1, Math.max(0, Math.ceil((p / 100) * s.length) - 1));
  return s[i]!;
}

export const median = (xs: readonly number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/** 바이트 · ms → kbps(1 kbps = 1000 bit/s) */
export const kbps = (bytes: number, ms: number) =>
  ms > 0 ? Math.round(((bytes * 8) / ms) * 10) / 10 : null;

const lower = (v: number | null, pass: number, warn: number): Verdict =>
  v == null ? "warn" : v <= pass ? "pass" : v <= warn ? "warn" : "fail";
const higher = (v: number | null, pass: number, warn: number): Verdict =>
  v == null ? "warn" : v >= pass ? "pass" : v >= warn ? "warn" : "fail";

export function levels(
  r: { rttMs: number | null; downKbps: number | null; upKbps: number | null },
  t: Thresholds = DEFAULT_THRESHOLDS,
): { rtt: Verdict; down: Verdict; up: Verdict } {
  return {
    rtt: lower(r.rttMs, t.rttPassMs, t.rttWarnMs),
    down: higher(r.downKbps, t.downPassKbps, t.downWarnKbps),
    up: higher(r.upKbps, t.upPassKbps, t.upWarnKbps),
  };
}

const RANK: Record<Verdict, number> = { pass: 0, warn: 1, fail: 2 };

/** 종합 — WSS 가 안 붙으면 어려움 · 그 밖은 셋 중 가장 나쁜 것(제어 평면 NetcheckService.verdict 와 같다) */
export function verdictOf(
  wss: WssResult,
  r: { rttMs: number | null; downKbps: number | null; upKbps: number | null },
  t: Thresholds = DEFAULT_THRESHOLDS,
): Verdict {
  if (wss !== "ok") return "fail";
  const l = levels(r, t);
  const worst = Math.max(RANK[l.rtt], RANK[l.down], RANK[l.up]);
  return worst === 0 ? "pass" : worst === 1 ? "warn" : "fail";
}

/** 주소의 실제 포트(기본 포트면 443 · 80) */
export function portOf(url: string): number | null {
  try {
    const u = new URL(url);
    if (u.port) return Number(u.port);
    return u.protocol === "https:" || u.protocol === "wss:" ? 443 : 80;
  } catch {
    return null;
  }
}

/** 단일 포트 — 셸(HTTPS)과 룸(WSS) 둘 다 암호화 443 */
export function singlePort(pageUrl: string, wsUrl: string | null): boolean {
  if (!wsUrl) return false;
  const a = new URL(pageUrl);
  const b = (() => {
    try {
      return new URL(wsUrl);
    } catch {
      return null;
    }
  })();
  return (
    !!b &&
    a.protocol === "https:" &&
    b.protocol === "wss:" &&
    portOf(pageUrl) === 443 &&
    portOf(wsUrl) === 443
  );
}

/** kbps → 'n.n Mbps' 또는 'n kbps' */
export function formatRate(k: number | null): string {
  if (k == null) return "—";
  return k >= 1000 ? `${(k / 1000).toFixed(1)} Mbps` : `${Math.round(k)} kbps`;
}
